import React, { useCallback, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import {
  getOptionDisplayDetails,
  WorkflowAssignmentAutocomplete,
  WorkflowAssignmentAutocompleteContext,
  WorkflowAssignmentGroupKey,
} from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import type { WorkflowAssignmentSelection } from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import { useSelectCurrentUser } from 'src/gql/hooks/user';
import { makeStyles } from 'tss-react/mui';

type UserOption = Extract<WorkflowAssignmentSelection, { __typename: 'User' }>;

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

export const ADD_ASSIGNEE_FOR_REVIEWER_TITLE = 'Add an assignee';
export const ADD_ASSIGNEE_FOR_REVIEWER_NOTE =
  "Choose an assignee who completes the task and responds to the reviewer's feedback, if any.";
const CHOOSE_ASSIGNEE_REASON = 'Choose an assignee';

interface AddAssigneeForReviewerDialogProps {
  /** Templates offer roles and start with nobody chosen; a workflow starts with the viewer. */
  isTemplate: boolean;
  /** Resolves false to keep the dialog open after a failed write. */
  onAdd: (assignee: WorkflowAssignmentSelection) => Promise<boolean>;
  onClose: () => void;
  reviewer: WorkflowAssignmentSelection | null;
  workflowId: UUID;
}

/**
 * Opens when the first reviewer is picked on a task with no assignee. The reviewer and the
 * assignee are written together, so Cancel adds nobody.
 */
export const AddAssigneeForReviewerDialog = React.memo(function AddAssigneeForReviewerDialog(
  props: AddAssigneeForReviewerDialogProps
): JSX.Element {
  const { isTemplate, onAdd, onClose, reviewer, workflowId } = props;
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [hasPicked, setHasPicked] = useState(false);
  const defaultAssignee = useMemo((): UserOption | null => {
    if (isTemplate || !currentUser) return null;

    return {
      __typename: 'User',
      avatarColor: currentUser.avatarColor,
      displayName: currentUser.displayName,
      email: currentUser.email,
      group: WorkflowAssignmentGroupKey.Users,
      id: currentUser.id,
    };
  }, [currentUser, isTemplate]);
  const [pickedAssignee, setPickedAssignee] = useState<WorkflowAssignmentSelection | null>(null);
  const assignee = getChosenAssignee({ defaultAssignee, hasPicked, pickedAssignee, reviewer });

  const handleExit = useCallback(() => {
    setPickedAssignee(null);
    setHasPicked(false);
  }, []);

  const handleChange = useCallback(
    (_event: React.ChangeEvent<unknown>, option: WorkflowAssignmentSelection | null) => {
      setPickedAssignee(option);
      setHasPicked(true);
    },
    []
  );

  const handleAdd = useCallback(async () => {
    if (!assignee) return false;
    return onAdd(assignee);
  }, [assignee, onAdd]);

  const excludedOptions = useMemo(() => (reviewer ? [reviewer] : []), [reviewer]);

  return (
    <ConfirmationDialog
      message={ADD_ASSIGNEE_FOR_REVIEWER_NOTE}
      open={Boolean(reviewer)}
      primaryDataCy="add-assignee-for-reviewer-submit"
      primaryDisabledReason={assignee ? '' : CHOOSE_ASSIGNEE_REASON}
      primaryText="Add"
      title={ADD_ASSIGNEE_FOR_REVIEWER_TITLE}
      variant={ConfirmationDialogVariant.Info}
      onClickPrimary={handleAdd}
      onClose={onClose}
      onExit={handleExit}
    >
      <div className={classes.body}>
        <Typography variant="body2">
          Reviewer: {getOptionDisplayDetails(reviewer).primaryText}
        </Typography>
        <WorkflowAssignmentAutocomplete
          excludeAlineAi
          autoFocus={!assignee}
          context={WorkflowAssignmentAutocompleteContext.WorkflowTask}
          dataCy="add-assignee-for-reviewer-autocomplete"
          excludedOptions={excludedOptions}
          multiple={false}
          placeholder="Choose an assignee..."
          value={assignee}
          workflowId={workflowId}
          onChange={handleChange}
        />
      </div>
    </ConfirmationDialog>
  );
});

/** The viewer stands in until someone else is picked, unless the viewer is the reviewer. */
function getChosenAssignee(options: {
  defaultAssignee: UserOption | null;
  hasPicked: boolean;
  pickedAssignee: WorkflowAssignmentSelection | null;
  reviewer: WorkflowAssignmentSelection | null;
}): WorkflowAssignmentSelection | null {
  const { defaultAssignee, hasPicked, pickedAssignee, reviewer } = options;
  if (hasPicked) return pickedAssignee;
  if (!defaultAssignee || !reviewer) return null;
  return getIsSamePerson(defaultAssignee, reviewer) ? null : defaultAssignee;
}

function getIsSamePerson(user: UserOption, other: WorkflowAssignmentSelection): boolean {
  switch (other.__typename) {
    case 'User':
      return other.id === user.id;
    case 'WorkflowPermission':
      return other.account?.__typename === 'User' && other.account.id === user.id;
    case 'AlineAi':
    case 'NewCollaborator':
      return false;
  }
}
